/**
 * What the agent did before it answered.
 *
 * A turn that takes eight seconds while a dot pulses is indistinguishable from
 * a turn that has hung. Naming the work — "Checking what is overdue", "Reading
 * the bank accounts" — turns the wait into progress, and it does something the
 * answer alone cannot: it shows WHICH records the answer came from, so a wrong
 * answer is traceable to a wrong lookup rather than to the model being the
 * model.
 *
 * While the turn runs, the steps are open. Once it finishes they collapse to a
 * single line, because the trace matters most in the moment and afterwards the
 * answer is the point.
 */

import { useState } from 'react';
import { Check, ChevronRight, Loader2, X } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';
import type { TraceStep } from './types';

export function Trace({ steps, live }: { steps: TraceStep[]; live: boolean }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  if (steps.length === 0) return null;

  // Live turns are always expanded — collapsing the thing somebody is waiting
  // on is exactly backwards.
  const expanded = live || open;
  const failed = steps.filter((s) => s.ok === false).length;

  return (
    <div className="mb-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={live}
        className={cn(
          'flex items-center gap-1.5 text-[11.5px] text-muted-foreground',
          !live && 'hover:text-foreground',
        )}
      >
        {live ? (
          <Loader2 className="size-3 animate-spin" aria-hidden />
        ) : (
          <ChevronRight
            className={cn('size-3 transition-transform rtl:rotate-180', open && 'rotate-90 rtl:rotate-90')}
            aria-hidden
          />
        )}
        <span>
          {live
            ? (steps.find((s) => s.running)?.label ?? t('agent_thinking'))
            : steps.length === 1
              ? t('agent_steps_one')
              : t('agent_steps').replace('{n}', String(steps.length))}
        </span>
        {!live && failed > 0 ? (
          <span className="text-destructive">·&nbsp;{t('agent_steps_failed')}</span>
        ) : null}
      </button>

      {expanded ? (
        <ol className="mt-1.5 space-y-1 border-s border-border ps-3">
          {steps.map((s, i) => (
            <li key={s.id ?? i} className="flex items-start gap-1.5 text-[11.5px]">
              <span className="mt-0.5 shrink-0">
                {s.running ? (
                  <Loader2 className="size-3 animate-spin text-muted-foreground" aria-hidden />
                ) : s.ok === false ? (
                  <X className="size-3 text-destructive" aria-hidden />
                ) : (
                  <Check className="size-3 text-muted-foreground" aria-hidden />
                )}
              </span>
              <span dir="auto" className="min-w-0">
                <span className="text-foreground/80">{s.label}</span>
                {s.summary && !s.running ? (
                  <span className="text-muted-foreground"> · {s.summary}</span>
                ) : null}
              </span>
            </li>
          ))}
        </ol>
      ) : null}
    </div>
  );
}
